/**
 * F3 propose (DESIGN §4.1, §4.2): one model call (streaming + retry-with-backoff
 * >=5, ~300s budget handled by the injected client) -> parse JSON -> validate
 * against the ProposalDelta union. Malformed-after-retry => cost-bearing failure
 * entry, zero deltas (the caller does no writes and does NOT advance the cursor).
 * The model PROPOSES; code APPLIES.
 */
import type { DreamModelClient, ProposalDelta, DigestEntry } from "./types";

export const MAX_MODEL_ATTEMPTS = 5;
const MODEL_NAME_FALLBACK = "gpt-5.4";

/** Default backoff: exponential 250ms·2^n, capped at 5s. Injected in tests. */
function defaultSleep(attempt: number): Promise<void> {
  const ms = Math.min(250 * 2 ** attempt, 5000);
  return new Promise((r) => setTimeout(r, ms));
}

// The locked DigestEntry folds no-op/rejected/failure into ONE member whose
// `kind` is the union "no-op" | "rejected" | "failure" — so a plain
// Extract<DigestEntry, { kind: "rejected" }> resolves to `never`. Intersect that
// combined member with the specific literal to recover the per-kind shape.
type DetailEntry = Extract<DigestEntry, { kind: "no-op" | "rejected" | "failure" }>;
type RejectedEntry = DetailEntry & { kind: "rejected" };
type FailureEntry = DetailEntry & { kind: "failure" };

export interface ProposeResult {
  deltas: ProposalDelta[];
  rejected: RejectedEntry[];
  cost: Extract<DigestEntry, { kind: "cost" }>;
  failure?: FailureEntry;
}

const STR = (v: unknown): v is string => typeof v === "string" && v.length > 0;
const STRARR = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");

/** Validate one model-emitted object against the locked ProposalDelta union. Returns the delta or a reason string. */
function validateDelta(d: Record<string, unknown>): ProposalDelta | string {
  switch (d.verb) {
    case "new":
      return STR(d.path) && STR(d.title) && STRARR(d.tags) && STR(d.body) && STRARR(d.evidence)
        ? { verb: "new", path: d.path, title: d.title, tags: d.tags, body: d.body, evidence: d.evidence }
        : "malformed 'new' delta (path/title/tags/body/evidence)";
    case "supersede":
      return STR(d.oldPath) && STR(d.newPath) && STR(d.title) && STRARR(d.tags) && STR(d.body) && STRARR(d.evidence)
        ? { verb: "supersede", oldPath: d.oldPath, newPath: d.newPath, title: d.title, tags: d.tags, body: d.body, evidence: d.evidence }
        : "malformed 'supersede' delta (oldPath/newPath/title/tags/body/evidence)";
    case "retract":
      return STR(d.path) && STR(d.reason) && STRARR(d.evidence)
        ? { verb: "retract", path: d.path, reason: d.reason, evidence: d.evidence }
        : "malformed 'retract' delta (path/reason/evidence)";
    case "skill-seed":
      return STR(d.slug) && STR(d.title) && STR(d.description) && d.description.length <= 60 && STRARR(d.tags) && STR(d.body) && STRARR(d.evidence)
        ? { verb: "skill-seed", slug: d.slug, title: d.title, description: d.description, tags: d.tags, body: d.body, evidence: d.evidence }
        : "malformed 'skill-seed' delta (slug/title/description<=60/tags/body/evidence)";
    case "flag":
      return STR(d.path) && STR(d.tension) && STRARR(d.evidence)
        ? { verb: "flag", path: d.path, tension: d.tension, evidence: d.evidence }
        : "malformed 'flag' delta (path/tension/evidence)";
    case "quarantine":
      return STR(d.slug) && STR(d.reason) && STR(d.body) && STRARR(d.evidence)
        ? { verb: "quarantine", slug: d.slug, reason: d.reason, body: d.body, evidence: d.evidence }
        : "malformed 'quarantine' delta (slug/reason/body/evidence)";
    case "no-op":
      return STR(d.detail) ? { verb: "no-op", detail: d.detail } : "malformed 'no-op' delta (detail)";
    default:
      return `unknown verb: ${String(d.verb)}`;
  }
}

/** Extract the first balanced JSON object from possibly-fenced model text. */
function extractJson(text: string): unknown {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/.exec(text);
  const candidate = (fenced ? fenced[1] : text).trim();
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start === -1 || end === -1 || end < start) throw new Error("no JSON object found");
  return JSON.parse(candidate.slice(start, end + 1));
}

export async function propose(
  model: DreamModelClient,
  system: string,
  user: string,
  opts: { sleep?: (attempt: number) => Promise<void>; model?: string; signal?: AbortSignal } = {},
): Promise<ProposeResult> {
  const sleep = opts.sleep ?? defaultSleep;
  const modelName = opts.model ?? MODEL_NAME_FALLBACK;
  let inputTokens = 0;
  let outputTokens = 0;
  let lastErr = "";
  const started = Date.now();

  for (let attempt = 0; attempt < MAX_MODEL_ATTEMPTS; attempt++) {
    const res = await model.complete({ system, user, signal: opts.signal });
    inputTokens += res.inputTokens;
    outputTokens += res.outputTokens;
    let parsed: unknown;
    try {
      parsed = extractJson(res.text);
    } catch (e) {
      lastErr = `parse: ${(e as Error).message}`;
      if (attempt < MAX_MODEL_ATTEMPTS - 1) { await sleep(attempt); continue; }
      break;
    }
    const rawDeltas = (parsed as { deltas?: unknown }).deltas;
    if (!Array.isArray(rawDeltas)) {
      lastErr = "schema: top-level { deltas: [] } missing";
      if (attempt < MAX_MODEL_ATTEMPTS - 1) { await sleep(attempt); continue; }
      break;
    }
    const deltas: ProposalDelta[] = [];
    const rejected: RejectedEntry[] = [];
    for (const raw of rawDeltas) {
      const v = validateDelta((raw ?? {}) as Record<string, unknown>);
      if (typeof v === "string") rejected.push({ kind: "rejected", detail: v });
      else deltas.push(v);
    }
    const cost: Extract<DigestEntry, { kind: "cost" }> = {
      kind: "cost", model: modelName, inputTokens, outputTokens, ms: Date.now() - started,
    };
    return { deltas, rejected, cost };
  }

  // Malformed after the full retry budget: cost-bearing failure, zero deltas.
  const failure: FailureEntry = {
    kind: "failure", detail: `malformed model output after ${String(MAX_MODEL_ATTEMPTS)} attempts: ${lastErr}`,
  };
  return {
    deltas: [],
    rejected: [],
    cost: { kind: "cost", model: modelName, inputTokens, outputTokens, ms: Date.now() - started },
    failure,
  };
}
